import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_URL = "https://suanuvem.com";
export const SITE_NAME = "Sua Nuvem";
export const DEFAULT_TITLE = "Sua Nuvem | Cloud Privada, DevOps, FinOps e Segurança";
export const DEFAULT_DESCRIPTION =
  "A Sua Nuvem implanta, gerencia e protege infraestrutura cloud privada, DevOps, FinOps e DevSecOps para empresas que precisam de performance, segurança e controle.";
export const DEFAULT_IMAGE = `${SITE_URL}/logo_suanuvem.png`;

export function normalizePath(pathname = "/") {
  if (/\.\w+$/.test(pathname)) {
    return pathname;
  }

  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

export function canonicalUrl(pathname = "/") {
  return `${SITE_URL}${normalizePath(pathname)}`;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
}

function setStructuredData(items) {
  document
    .querySelectorAll('script[type="application/ld+json"][data-seo="true"]')
    .forEach((element) => element.remove());

  items.filter(Boolean).forEach((item) => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", "true");
    script.text = JSON.stringify(item);
    document.head.appendChild(script);
  });
}

export function organizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    sameAs: [
      "https://www.instagram.com/suanuvem.io/",
      "https://www.linkedin.com/company/sua-nuvem/posts/?feedView=all",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+55-84-98638-0832",
      contactType: "sales",
      areaServed: "BR",
      availableLanguage: "pt-BR",
    },
    address: {
      "@type": "PostalAddress",
      streetAddress: "Av. Santos Dumont, 1560",
      addressLocality: "Macaiba",
      addressRegion: "RN",
      postalCode: "59280-000",
      addressCountry: "BR",
    },
  };
}

export function websiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    inLanguage: "pt-BR",
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

export function serviceSchema(service, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.description,
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: SITE_URL,
    },
    areaServed: "BR",
    url: canonicalUrl(path),
    serviceType: service.title,
  };
}

export function faqSchema(items = []) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
}

export function articleSchema(post, path) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    author: {
      "@type": "Organization",
      name: post.author,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: {
        "@type": "ImageObject",
        url: DEFAULT_IMAGE,
      },
    },
    mainEntityOfPage: canonicalUrl(path),
    image: DEFAULT_IMAGE,
    inLanguage: "pt-BR",
  };
}

export function usePageSeo({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  path,
  type = "website",
  image = DEFAULT_IMAGE,
  robots = "index, follow",
  structuredData = [],
}) {
  const location = useLocation();
  const pagePath = path || location.pathname;
  const pageUrl = canonicalUrl(pagePath);

  useEffect(() => {
    document.documentElement.lang = "pt-BR";
    document.title = title;

    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="author"]', { name: "author", content: SITE_NAME });

    upsertLink('link[rel="canonical"]', { rel: "canonical", href: pageUrl });

    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "pt_BR" });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: pageUrl });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: image });

    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    setStructuredData(structuredData);
  }, [description, image, pageUrl, robots, structuredData, title, type]);
}
